/**
 * Back-off after a 429 from the API (2026-11-04).
 *
 * The compat layer allows 30 REST requests per minute per token, and every tab
 * of a browser profile shares the token, so a few open tabs can exceed it. It
 * answers `429 {error, retry_after}` with a Retry-After header; the header is
 * not exposed over CORS, so in a browser the body is the value that can be read.
 *
 * Kept per tab and in memory: the limit's counters reset within a minute, and a
 * reload simply asks again. Free of app and DOM dependencies.
 */

const DEFAULT_RETRY_AFTER_SECONDS = 60;
const MAX_RETRY_AFTER_SECONDS = 300;

let limitedUntilMs = 0;

function retryAfterSeconds(response) {
  const raw = response?.headers?.['retry-after'] ?? response?.data?.retry_after;
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_RETRY_AFTER_SECONDS;
  return Math.min(seconds, MAX_RETRY_AFTER_SECONDS);
}

/**
 * Start (or extend) the back-off from a 429 response.
 * @param {object} response - The axios error's `response`
 * @param {number} [nowMs]
 * @returns {void}
 */
export function noteRateLimited(response, nowMs = Date.now()) {
  limitedUntilMs = Math.max(limitedUntilMs, nowMs + retryAfterSeconds(response) * 1000);
}

/**
 * @param {number} [nowMs]
 * @returns {number} ms left in the back-off, 0 when there is none
 */
export function rateLimitRemainingMs(nowMs = Date.now()) {
  return Math.max(limitedUntilMs - nowMs, 0);
}

/**
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function isRateLimited(nowMs = Date.now()) {
  return rateLimitRemainingMs(nowMs) > 0;
}

/** End the back-off (an explicit Retry, or a different account). */
export function clearRateLimit() {
  limitedUntilMs = 0;
}
